/******************************************************************************************** */
/* Role Check functions */
/******************************************************************************************** */
export const isNonStaffUser = (role) => {
	return role === "Staff Non User";
};

export const isStaffUser = (role) => {
	return role === "Staff User";
};

/** Staff see only their own tasks; admins/supervisors see organisation-wide tasks. */
export const canViewAllTasks = (role) => !isStaffUser(role);

/** True when the user supervises at least one staff member (from profile.supervisingStaffList). */
export const isSupervisor = (supervisingStaffList) =>
	Array.isArray(supervisingStaffList) && supervisingStaffList.length > 0;

/** Supervisors (staff users with supervisees) get limited admin timesheet capabilities. */
export const canManageSupervisedTimesheets = (role, supervisingStaffList) =>
	!isStaffUser(role) || isSupervisor(supervisingStaffList);

/**
 * Staff directory visibility.
 * Account admins, super admins, consultants, and other non-staff roles see the organisation.
 * Staff users see the menu and page only when they have direct reports.
 */
export const canViewStaffDirectory = (role, supervisingStaffList) =>
	!isStaffUser(role) || isSupervisor(supervisingStaffList);

/**
 * Staff ids a viewer may open in the directory.
 * Null means the full organisation. An empty list means the directory is closed.
 */
export const getVisibleStaffDirectoryIds = (role, supervisingStaffList) => {
	if (!isStaffUser(role)) {
		return null;
	}
	if (!Array.isArray(supervisingStaffList)) {
		return [];
	}
	return supervisingStaffList.map((id) => String(id).toLowerCase());
};

/** directoryIds null means the full organisation. Otherwise match direct-report ids. */
export const isInStaffDirectory = (directoryIds, staffId) =>
	directoryIds == null ||
	directoryIds.includes(String(staffId).toLowerCase());

/** Add, edit, and deactivate staff records. Account administrators and super admins only. */
export const canManageStaff = (role) =>
	isAccountAdminUser(role) || isSuperAdminUser(role);

/**
 * Whether the current user may approve/unapprove a timesheet entry.
 * Account admins and super admins: any closed work timesheet.
 * Direct supervisors: supervisees only (not their own).
 */
export const canApproveTimesheet = ({
	role,
	supervisingStaffList,
	currentStaffId,
	timesheetStaffId,
}) => {
	if (timesheetStaffId == null || timesheetStaffId === "") {
		return false;
	}

	const timesheetStaffKey = String(timesheetStaffId);
	const isOwn =
		currentStaffId != null &&
		currentStaffId !== "" &&
		String(currentStaffId) === timesheetStaffKey;

	if (isAccountAdminUser(role) || isSuperAdminUser(role)) {
		return true;
	}

	if (isOwn) {
		return false;
	}

	return (
		Array.isArray(supervisingStaffList) &&
		supervisingStaffList.some((id) => String(id) === timesheetStaffKey)
	);
};

export const getTasksNavTitle = (role) => (isStaffUser(role) ? "My Tasks" : "Tasks");

export const isAccountAdminUser = (role) => {
	return role === "Account Administrator";
};

export const isSuperAdminUser = (role) => {
	return role === "Super Admin";
};

export const isConsultantUser = (role) => {
	return role === "Consultant";
};

/** Account menu Help / feedback - admins only, not staff. */
export const canSubmitProductFeedback = (role) =>
	isAccountAdminUser(role) || isSuperAdminUser(role) || isConsultantUser(role);

/** Super Admin, Account Administrator, Consultant, and Staff Non User — not Staff User. */
export const canManageOrganisationRecords = (role) => !isStaffUser(role);

/** Pin / unpin photos-notes to sites, customers, or staff (admin-level, not staff). */
export const canPinJobs = (role) => !isStaffUser(role);
